import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router'
import { escritorio } from '../../lib/desktop'
import { hayHistorialInterno, padreLogico } from './rutaMigas'

// Comportamiento de navegación de toda la app, montado UNA vez en App.tsx:
//  · el scroll vuelve a donde estaba al regresar (y sube al entrar a otra página);
//  · atajos de atrás/adelante en la app de escritorio.
// No pinta nada.

// ─── Scroll ──────────────────────────────────────────────────────────────────
//
// POR QUÉ a mano: BrowserRouter no restaura el scroll (el <ScrollRestoration> de
// React Router solo existe con el router de datos, y mudar el router entero por
// esto no se justifica). Y el del navegador no sirve en una SPA: al "atrás" intenta
// restaurar ANTES de que la página vuelva a pintar sus datos, la página todavía
// mide una pantalla y el scroll cae arriba. Aquí se guarda la posición por entrada
// del historial (location.key) y al volver se espera a que la página crezca.

const LLAVE = 'camp_scroll'
const MAX_ENTRADAS = 60
/** Cuánto se espera a que la página vuelva a medir lo que medía. */
const ESPERA_MAX_MS = 2500

function leerGuardadas(): Map<string, number> {
  try {
    const crudo = sessionStorage.getItem(LLAVE)
    const obj = crudo ? (JSON.parse(crudo) as Record<string, unknown>) : {}
    return new Map(
      Object.entries(obj).filter((e): e is [string, number] => typeof e[1] === 'number' && Number.isFinite(e[1])),
    )
  } catch {
    return new Map()
  }
}

function guardar(pos: Map<string, number>) {
  try {
    // Solo las últimas: sessionStorage es por pestaña y no debe crecer sin techo.
    const recientes = Array.from(pos.entries()).slice(-MAX_ENTRADAS)
    sessionStorage.setItem(LLAVE, JSON.stringify(Object.fromEntries(recientes)))
  } catch {
    /* modo privado o storage lleno: se restaura solo dentro de esta carga */
  }
}

/** Sube la posición al final del Map (orden de inserción = antigüedad). */
function anotar(pos: Map<string, number>, clave: string, y: number) {
  pos.delete(clave)
  pos.set(clave, Math.max(0, Math.round(y)))
}

/**
 * Intenta llegar a `y`. Si la página todavía no mide lo suficiente (sus datos
 * vienen en camino), reintenta cada cuadro hasta alcanzarla, hasta ESPERA_MAX_MS,
 * o hasta que la persona se mueva sola: pelearle el scroll a quien ya empezó a
 * desplazarse es peor que no restaurar.
 */
function restaurar(y: number): () => void {
  let cuadro = 0
  let vivo = true
  const inicio = performance.now()
  const soltar = () => {
    vivo = false
    if (cuadro) cancelAnimationFrame(cuadro)
    quitar()
  }
  const eventos = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const
  const quitar = () => eventos.forEach((ev) => window.removeEventListener(ev, soltar, true))
  eventos.forEach((ev) => window.addEventListener(ev, soltar, { capture: true, passive: true }))

  const intento = () => {
    cuadro = 0
    if (!vivo) return
    const alcanzable = document.documentElement.scrollHeight - window.innerHeight
    window.scrollTo(0, Math.min(y, Math.max(0, alcanzable)))
    if (alcanzable >= y - 1 || performance.now() - inicio > ESPERA_MAX_MS) {
      soltar()
      return
    }
    cuadro = requestAnimationFrame(intento)
  }
  intento()
  return soltar
}

/**
 * Llave de la posición guardada. NO basta `location.key`: React Router le pone
 * "default" a TODA entrada que se abrió cargando la página (liga pegada, recarga,
 * la app recién abierta), así que dos páginas distintas abiertas así en la misma
 * pestaña compartían llave y la segunda aparecía a media página, en el scroll de
 * la primera. Con la ruta en la llave cada una guarda lo suyo, y recargar la
 * misma página sigue regresando a donde estaba.
 */
export function llaveScroll(key: string, pathname: string, search: string): string {
  return `${key} ${pathname}${search}`
}

function useRestaurarScroll() {
  const { key: llaveHistorial, pathname, search, hash } = useLocation()
  const key = llaveScroll(llaveHistorial, pathname, search)
  const tipo = useNavigationType()
  const posiciones = useRef<Map<string, number> | null>(null)
  if (posiciones.current === null) posiciones.current = leerGuardadas()
  const claveActual = useRef(key)
  const rutaPrevia = useRef<string | null>(null)

  useEffect(() => {
    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
    } catch {
      /* hay navegadores embebidos que no lo dejan escribir */
    }
    // Se anota en el acto, sin esperar cuadro: el navegador ya entrega 'scroll' a
    // lo más una vez por cuadro, anotar es un Map.set, y con requestAnimationFrame
    // una ventana en segundo plano (que no pinta cuadros) perdía la posición.
    const alDesplazar = () => anotar(posiciones.current!, claveActual.current, window.scrollY)
    const alSalir = () => {
      anotar(posiciones.current!, claveActual.current, window.scrollY)
      guardar(posiciones.current!)
    }
    window.addEventListener('scroll', alDesplazar, { passive: true })
    window.addEventListener('pagehide', alSalir)
    return () => {
      window.removeEventListener('scroll', alDesplazar)
      window.removeEventListener('pagehide', alSalir)
    }
  }, [])

  // Layout y no effect: la clave tiene que cambiar ANTES de que el navegador
  // dispare el scroll que provoca la página nueva al medir distinto; si no, ese
  // scroll se anotaría en la entrada de la que se viene y "atrás" caería mal.
  useLayoutEffect(() => {
    const pos = posiciones.current!
    claveActual.current = key
    const cambioDePagina = rutaPrevia.current !== pathname
    rutaPrevia.current = pathname
    guardar(pos)

    if (tipo === 'POP') {
      const y = pos.get(key)
      if (y !== undefined && y > 0) return restaurar(y)
      return
    }
    // Página nueva: arriba. Con #ancla no: la herramienta salta sola a la idea o
    // al comentario, y subir aquí le ganaría la carrera.
    // Solo cambiar la query (?post=, filtros) no es página nueva: no se mueve.
    if (cambioDePagina && !hash) window.scrollTo(0, 0)
  }, [key]) // solo al cambiar de entrada del historial, a propósito
}

// ─── Atajos ──────────────────────────────────────────────────────────────────
//
// SOLO en la app de escritorio. En el navegador, ⌘[ / ⌘← / Alt+← ya son "atrás"
// del propio navegador —y hacen lo mismo, porque la app empuja cada pantalla al
// historial—; escucharlos aquí también retrocedería DOS veces. En la app de
// escritorio no hay barra del navegador ni esos atajos (el menú por defecto de
// Electron no trae Atrás), así que ahí los ponemos nosotros, con las teclas de
// cada sistema: ⌘[ y ⌘← en Mac, Alt+← en Windows y Linux.

export function esEditable(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false
  if (t.isContentEditable) return true
  return Boolean(t.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])'))
}

export function direccionDeAtajo(e: KeyboardEvent, mac: boolean): 'atras' | 'adelante' | null {
  if (e.defaultPrevented || e.isComposing || e.shiftKey) return null
  if (mac) {
    if (!e.metaKey || e.altKey || e.ctrlKey) return null
    if (e.key === '[' || e.key === 'ArrowLeft') return 'atras'
    if (e.key === ']' || e.key === 'ArrowRight') return 'adelante'
    return null
  }
  if (!e.altKey || e.metaKey || e.ctrlKey) return null
  if (e.key === 'ArrowLeft') return 'atras'
  if (e.key === 'ArrowRight') return 'adelante'
  return null
}

function useAtajosNavegacion() {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const ruta = useRef({ pathname, search })
  ruta.current = { pathname, search }

  useEffect(() => {
    const d = escritorio()
    if (!d) return
    const mac = d.platform === 'darwin'
    const alTeclear = (e: KeyboardEvent) => {
      const dir = direccionDeAtajo(e, mac)
      if (!dir) return
      // ⌘← dentro de un campo es "inicio de renglón": jamás navegar ahí.
      if (esEditable(e.target)) return
      // Con un diálogo abierto, navegar lo desmontaría con lo que se esté
      // escribiendo adentro. Primero se cierra el diálogo.
      if (document.querySelector('[aria-modal="true"]')) return
      e.preventDefault()
      if (dir === 'adelante') {
        navigate(1)
        return
      }
      if (hayHistorialInterno()) navigate(-1)
      else {
        const padre = padreLogico(ruta.current.pathname, ruta.current.search)
        if (padre?.to) navigate(padre.to)
      }
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  }, [navigate])
}

export function NavegacionGlobal(): null {
  useRestaurarScroll()
  useAtajosNavegacion()
  return null
}
